import React, { useState } from "react";
import axios from "axios";
import Joi from "joi";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  Paper,
  Snackbar,
  TextField,
  Alert,
  IconButton,
  InputAdornment,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import BackBtn from "../../widgets/BackBtn";
import PageTitle from "../../widgets/PageTitle";
import { API_ENDPOINT } from "../../utils/constants";
import { getHeader } from "../../utils/util";

/** Joi Validation Schema */
const resellerValidation = Joi.object({
  username: Joi.string()
    .max(20)
    .pattern(/^[a-zA-Z0-9\s]+$/)
    .required()
    .label("Username")
    .messages({
      "string.pattern.base":
        "Username can only contain alphabets, numbers, and spaces",
    }),
  firstName: Joi.string().max(50).required().label("First Name"),
  lastName: Joi.string().max(50).required().label("Last Name"),
  companyName: Joi.string().max(100).required().label("Company Name"),
  password: Joi.string()
    .min(8)
    .pattern(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d@$!%*?&]{8,}$/)
    .required()
    .label("Password")
    .messages({
      "string.pattern.base":
        "Password must contain at least one uppercase letter, one lowercase letter, one number, and may include special characters",
    }),
});

/** Centralized initial state */
const initialForm = {
  username: "",
  firstName: "",
  lastName: "",
  companyName: "",
  password: "",
};

const AddReseller = () => {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [snack, setSnack] = useState({
    open: false,
    msg: "",
    severity: "success",
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false); // New state for toggle

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      const newErrors = { ...errors };
      delete newErrors[name];
      setErrors(newErrors);
    }
  };

  const validate = () => {
    const { error } = resellerValidation.validate(form, { abortEarly: false });
    if (!error) return true;
    const newErrors = {};
    error.details.forEach((detail) => {
      const field = detail.path[0];
      newErrors[field] = detail.message;
    });
    setErrors(newErrors);
    return false;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate with Joi
    if (!validate()) {
      setSnack({
        open: true,
        msg: "Please fix the errors in the form",
        severity: "warning",
      });
      return;
    }

    try {
      setSubmitting(true);
      await axios.post(`${API_ENDPOINT}/user/add-reseller`, form, getHeader());
      setSnack({
        open: true,
        msg: "Reseller added successfully!",
        severity: "success",
      });
      setForm(initialForm);
      setErrors({});
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.message ||
        "Something went wrong while saving.";
      setSnack({ open: true, msg, severity: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container sx={{ my: 3 }}>
      <BackBtn />
      <Container maxWidth="md">
        <PageTitle sx={{ mt: 1 }}>Add Reseller</PageTitle>
        <Paper variant="outlined" sx={{ p: 3, mt: 3 }}>
          <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              {/* Username */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Reseller Username"
                  name="username"
                  value={form.username}
                  onChange={handleChange}
                  fullWidth
                  required
                  size="small"
                  error={Boolean(errors.username)}
                  helperText={
                    errors.username ||
                    "Alphabets, numbers, spaces only (max 20 chars)"
                  }
                  inputProps={{ maxLength: 20 }}
                />
              </Grid>

              {/* First Name */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="First Name"
                  name="firstName"
                  value={form.firstName}
                  onChange={handleChange}
                  fullWidth
                  required
                  size="small"
                  error={Boolean(errors.firstName)}
                  helperText={errors.firstName}
                  inputProps={{ maxLength: 50 }}
                />
              </Grid>

              {/* Last Name */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Last Name"
                  name="lastName"
                  value={form.lastName}
                  onChange={handleChange}
                  fullWidth
                  required
                  size="small"
                  error={Boolean(errors.lastName)}
                  helperText={errors.lastName}
                  inputProps={{ maxLength: 50 }}
                />
              </Grid>

              {/* Company Name */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Company Name"
                  name="companyName"
                  value={form.companyName}
                  onChange={handleChange}
                  fullWidth
                  required
                  size="small"
                  error={Boolean(errors.companyName)}
                  helperText={errors.companyName}
                  inputProps={{ maxLength: 100 }}
                />
              </Grid>

              {/* Login Password */}
              <Grid item xs={12}>
                <TextField
                  label="Login Password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={form.password}
                  onChange={handleChange}
                  fullWidth
                  required
                  size="small"
                  error={Boolean(errors.password)}
                  helperText={
                    errors.password ||
                    "Min 8 chars: 1 uppercase, 1 lowercase, 1 number, optional special chars"
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} sx={{ textAlign: "right" }}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={submitting}
                  startIcon={submitting && <CircularProgress size={18} />}
                >
                  {submitting ? "Saving…" : "Save Reseller"}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Container>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          severity={snack.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AddReseller;
